'use client';

type SettingsColorTagProps = {
  /** Tag name. */
  label: string;
  /** The tag's own colour, as stored — a data value, not a design token. */
  color?: string;
  className?: string;
};

/**
 * The prototype's `.chip2`, drawn as Figma's small outlined tag: a colour dot and the tag name, used
 * wherever the settings design shows a user-defined tag.
 *
 * DS-GAP(Tag with a record-supplied colour): delete this when DS Tag takes a colour it is given.
 *
 * Checked against DS 0.3.1's `Tag`, which cannot express this: its `color` prop
 * is the six semantic tones (info, neutral, success, warning, danger, ai), each
 * one a `tag-<tone>` class binding fill and text to that tone's tokens, and a
 * user-defined tag's colour is a stored value with no tone behind it. The dot
 * would also have to ride in Tag's `icon` slot, which the component force-sizes
 * to the icon scale (20px, 16px from the md breakpoint) rather than the 8px the
 * design draws. `Chip` is not the alternative either — it is a button with
 * selected / editing / removable states, where this one only displays a name.
 *
 * The dot's colour comes from the record, so it is the one inline style in this
 * kit; the pill itself is DS tokens throughout. `currentColor` is the fallback
 * so a tag with no stored colour still reads as a dot rather than vanishing.
 */
export const SettingsColorTag = ({ label, color, className }: SettingsColorTagProps) => (
  <span
    data-lula-ds=""
    // A block-level box (`flex w-fit`), not inline-flex: an inline box sits on the cell's text
    // baseline and rides above the row's centre. 24px and 12px text, a small corner and an
    // outline, as Figma draws its tag (the frame's blue is not used: the dot is the tag's own).
    className={`typography-body-xs mb-0 flex w-fit items-center gap-space-6 h-6 rounded-md border border-solid border-border-default bg-bg-surface px-space-8 text-text-primary${
      className ? ` ${className}` : ''
    }`}
  >
    <span
      aria-hidden="true"
      className="size-space-8 shrink-0 rounded-full"
      style={{ backgroundColor: color || 'currentColor' }}
    />
    {label}
  </span>
);

export default SettingsColorTag;
